import React, { useEffect, useState, useCallback } from 'react';
import {
  getPlans,
  getPlan,
  createPlan,
  updatePlan,
  deletePlan,
  synthesizePlan,
} from '../services/sevenGenService';
import { getTenants } from '../services/tenantService';
import GenerationTimeline from '../components/sevengen/GenerationTimeline';
import PlanEditor, { CATEGORY_COLOR, CATEGORIES } from '../components/sevengen/PlanEditor';

// ─── Pill badge ───────────────────────────────────────────────────────────────
function Badge({ label, color }) {
  return (
    <span style={{
      background: `${color}22`,
      color,
      border: `1px solid ${color}55`,
      borderRadius: 20,
      padding: '2px 9px',
      fontSize: 11,
      fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

// ─── Plan list card ───────────────────────────────────────────────────────────
const STATUS_COLOR = { draft: '#94a3b8', active: '#22c55e', archived: '#475569' };

function PlanCard({ plan, isSelected, onClick, onDelete, isAdmin }) {
  const accent = CATEGORY_COLOR[plan.category] || '#6366f1';
  const catLabel = CATEGORIES.find(c => c.value === plan.category)?.label || plan.category;
  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? '#1e3a5f' : '#1e293b',
        border: `1px solid ${isSelected ? accent : '#334155'}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        padding: '12px 14px',
        cursor: 'pointer',
        marginBottom: 8,
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14, marginBottom: 4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {plan.title}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 5 }}>
            <Badge label={catLabel} color={accent} />
            <Badge label={plan.status} color={STATUS_COLOR[plan.status] || '#94a3b8'} />
            {plan.tenantId && (
              <Badge label={plan.tenantId.name || 'Community'} color="#0ea5e9" />
            )}
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {plan.generationCount} gen · {plan.baseYear}–{plan.baseYear + plan.generationCount * 25 - 1}
            {plan.createdBy && ` · ${plan.createdBy.name}`}
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(plan._id); }}
            title="Delete plan"
            style={{ background: 'none', border: 'none', color: '#64748b',
              cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}
          >
            ×
          </button>
        )}
      </div>
      {plan.tags?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          {plan.tags.map(t => (
            <span key={t} style={{ fontSize: 10, color: '#475569' }}>#{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Status editor chips ──────────────────────────────────────────────────────
function StatusToggle({ status, onChange }) {
  const options = ['draft', 'active', 'archived'];
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(s => (
        <button
          key={s}
          onClick={() => onChange(s)}
          style={{
            background: status === s ? `${STATUS_COLOR[s]}33` : 'transparent',
            border: `1px solid ${status === s ? STATUS_COLOR[s] : '#334155'}`,
            color: status === s ? STATUS_COLOR[s] : '#64748b',
            borderRadius: 20,
            padding: '3px 12px',
            fontSize: 12,
            cursor: 'pointer',
            fontWeight: status === s ? 700 : 400,
          }}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ─── Lens Snapshot panel ──────────────────────────────────────────────────────
function LensSnapshotPanel({ snapshot, onSynthesize, syncing }) {
  if (!snapshot?.capturedAt) {
    return (
      <div style={{ background: '#1e293b', borderRadius: 8, padding: 16, marginBottom: 20,
        border: '1px dashed #334155' }}>
        <div style={{ color: '#64748b', fontSize: 13, marginBottom: 10 }}>
          No lens data linked to this plan yet.
        </div>
        <button onClick={onSynthesize} disabled={syncing}
          style={{ background: '#6366f1', border: 'none', color: '#fff',
            borderRadius: 7, padding: '7px 16px', cursor: syncing ? 'wait' : 'pointer',
            fontSize: 13, fontWeight: 600, opacity: syncing ? 0.7 : 1 }}>
          {syncing ? '⟳ Capturing…' : '🔭 Capture Lens Snapshot'}
        </button>
      </div>
    );
  }

  const captured = new Date(snapshot.capturedAt).toLocaleDateString();
  const accent = '#6366f1';

  const SnapRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0',
      borderBottom: '1px solid #1e293b', fontSize: 13 }}>
      <span style={{ color: '#94a3b8' }}>{label}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div style={{ background: '#1e293b', borderRadius: 8, padding: 16, marginBottom: 20,
      borderTop: `3px solid ${accent}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12 }}>
        <span style={{ color: '#a5b4fc', fontWeight: 700, fontSize: 13 }}>
          🔭 Lens Snapshot — {captured}
        </span>
        <button onClick={onSynthesize} disabled={syncing}
          style={{ background: 'transparent', border: `1px solid ${accent}`, color: accent,
            borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: syncing ? 'wait' : 'pointer' }}>
          {syncing ? '⟳' : 'Refresh'}
        </button>
      </div>
      <SnapRow label="Communities tracked" value={snapshot.communityCount ?? '—'} />
      <SnapRow label="Environmental records" value={snapshot.environmentalCount ?? '—'} />
      <SnapRow label="Traditional-use records" value={snapshot.traditionalSiteCount ?? '—'} />
      {snapshot.topSpecies?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 5 }}>TOP SPECIES</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {snapshot.topSpecies.slice(0, 5).map((s, i) => (
              <Badge key={i} label={`${s._id || 'Unknown'} (${s.count})`} color="#16a34a" />
            ))}
          </div>
        </div>
      )}
      {snapshot.topSeasons?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 5 }}>TOP SEASONS</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {snapshot.topSeasons.map((s, i) => (
              <Badge key={i} label={`${s._id} (${s.count})`} color="#ea580c" />
            ))}
          </div>
        </div>
      )}
      {snapshot.summary && (
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, marginBottom: 0 }}>
          {snapshot.summary}
        </p>
      )}
    </div>
  );
}

// ─── Detail view (right pane) ─────────────────────────────────────────────────
function PlanDetail({ planId, tenants, isAdmin, onUpdated, onClose }) {
  const [plan,       setPlan]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [syncing,    setSyncing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [editTitle,  setEditTitle]  = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPlan(planId);
      setPlan(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const handlePhaseSave = async (genNumber, fields) => {
    const updatedPhases = plan.phases.map(p =>
      p.generation === genNumber ? { ...p, ...fields } : p
    );
    setSaving(true);
    try {
      const res = await updatePlan(planId, { phases: updatedPhases });
      setPlan(res.data);
      onUpdated();
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await updatePlan(planId, { status: newStatus });
      setPlan(res.data);
      onUpdated();
    } catch { /* ignore */ }
  };

  const handleSaveTitle = async () => {
    if (!titleDraft.trim()) return;
    try {
      const res = await updatePlan(planId, { title: titleDraft.trim() });
      setPlan(res.data);
      onUpdated();
      setEditTitle(false);
    } catch { /* ignore */ }
  };

  const handleSynthesize = async () => {
    setSyncing(true);
    try {
      await synthesizePlan(planId, {});
      await loadPlan();
      onUpdated();
    } catch { /* ignore */ } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#64748b' }}>
        Loading plan…
      </div>
    );
  }
  if (!plan) return null;

  const accent = CATEGORY_COLOR[plan.category] || '#6366f1';
  const catLabel = CATEGORIES.find(c => c.value === plan.category)?.label || plan.category;
  const communityName = plan.tenantId?.name
    || tenants.find(t => t._id === plan.tenantId)?.name
    || 'Cross-community';

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 40px' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 20, gap: 12 }}>
        <div style={{ flex: 1 }}>
          {editTitle ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                style={{ flex: 1, background: '#1e293b', border: `1px solid ${accent}`,
                  borderRadius: 6, color: '#f1f5f9', padding: '6px 10px', fontSize: 18,
                  fontWeight: 700 }}
                autoFocus
              />
              <button onClick={handleSaveTitle}
                style={{ background: accent, border: 'none', color: '#fff',
                  borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600 }}>
                Save
              </button>
              <button onClick={() => setEditTitle(false)}
                style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
                  borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                ×
              </button>
            </div>
          ) : (
            <h2
              onClick={() => { setTitleDraft(plan.title); setEditTitle(true); }}
              title="Click to rename"
              style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700,
                cursor: 'text', display: 'inline-block' }}
            >
              {plan.title}
              {saving && <span style={{ color: '#64748b', fontSize: 13, marginLeft: 8 }}>saving…</span>}
            </h2>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <Badge label={catLabel} color={accent} />
            <Badge label={communityName} color="#0ea5e9" />
            {plan.tags?.map(t => (
              <span key={t} style={{ fontSize: 11, color: '#475569' }}>#{t}</span>
            ))}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#64748b',
            fontSize: 20, cursor: 'pointer', padding: 4, flexShrink: 0 }}>
          ←
        </button>
      </div>

      {/* Status */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>STATUS</div>
        <StatusToggle status={plan.status} onChange={handleStatusChange} />
      </div>

      {/* Description */}
      {plan.description && (
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          {plan.description}
        </p>
      )}

      {/* Lens snapshot */}
      <LensSnapshotPanel
        snapshot={plan.lensSnapshot}
        onSynthesize={handleSynthesize}
        syncing={syncing}
      />

      {/* Timeline */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          SEVEN GENERATIONS — {plan.baseYear} to {plan.baseYear + plan.generationCount * 25 - 1}
        </div>
        <GenerationTimeline
          phases={plan.phases}
          readonly={false}
          onPhaseSave={handlePhaseSave}
        />
      </div>
    </div>
  );
}

// ─── Filters toolbar ──────────────────────────────────────────────────────────
function FiltersBar({ filters, onChange, tenants }) {
  const f = (key) => (e) => onChange({ ...filters, [key]: e.target.value });
  const selectStyle = {
    background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
    color: '#e2e8f0', padding: '6px 10px', fontSize: 13, cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
      <select value={filters.status} onChange={f('status')} style={selectStyle}>
        <option value="">All statuses</option>
        <option value="draft">Draft</option>
        <option value="active">Active</option>
        <option value="archived">Archived</option>
      </select>
      <select value={filters.category} onChange={f('category')} style={selectStyle}>
        <option value="">All categories</option>
        {CATEGORIES.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      {tenants.length > 0 && (
        <select value={filters.tenantId} onChange={f('tenantId')} style={selectStyle}>
          <option value="">All communities</option>
          {tenants.map(t => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SevenGenPage() {
  const [plans,       setPlans]       = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedId,  setSelectedId]  = useState(null);
  const [showCreator, setShowCreator] = useState(false);
  const [filters,     setFilters]     = useState({ status: '', category: '', tenantId: '' });

  // Derive isAdmin from the JWT stored in localStorage — true enforcement is on the backend.
  const isAdmin = (() => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return false;
      const payload = JSON.parse(atob(t.split('.')[1]));
      return payload.globalRole === 'cip_admin';
    } catch { return false; }
  })();

  const loadPlans = useCallback(async () => {
    try {
      const params = {};
      if (filters.status)   params.status   = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.tenantId) params.tenantId = filters.tenantId;
      const res = await getPlans(params);
      setPlans(res.data);
    } catch { /* ignore */ }
  }, [filters]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [plansRes, tenantsRes] = await Promise.all([
          getPlans({}),
          getTenants(),
        ]);
        setPlans(plansRes.data);
        setTenants(tenantsRes.data);
      } catch { /* ignore */ } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Reload when filters change (after initial load)
  useEffect(() => {
    if (!loading) loadPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleCreate = async (data) => {
    const res = await createPlan(data);
    setPlans(prev => [res.data, ...prev]);
    setSelectedId(res.data._id);
    setShowCreator(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try {
      await deletePlan(id);
      setPlans(prev => prev.filter(p => p._id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div style={{ padding: 32, color: '#64748b', textAlign: 'center' }}>
        Loading Seven-Generation Plans…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 0, height: '100%', minHeight: 0 }}>
      {/* ── Left pane: plan list ─────────────────────────────────────────── */}
      <div
        style={{
          width: selectedId ? 320 : '100%',
          minWidth: selectedId ? 280 : undefined,
          maxWidth: selectedId ? 340 : undefined,
          flexShrink: 0,
          padding: '24px 20px',
          borderRight: selectedId ? '1px solid #1e293b' : 'none',
          overflowY: 'auto',
          transition: 'width 0.2s',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 18 }}>
          <div>
            <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: selectedId ? 16 : 22,
              fontWeight: 700 }}>
              🏹 Seven-Generation Planning
            </h2>
            {!selectedId && (
              <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
                Long-horizon strategic plans built on lens intelligence —
                evaluating community decisions across seven generations (~175 years).
              </p>
            )}
          </div>
          <button
            onClick={() => setShowCreator(true)}
            style={{ background: '#6366f1', border: 'none', color: '#fff',
              borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
              fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            + New Plan
          </button>
        </div>

        {!selectedId && (
          <FiltersBar filters={filters} onChange={setFilters} tenants={tenants} />
        )}

        {plans.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 20px',
            border: '1px dashed #334155', borderRadius: 10 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No plans yet</div>
            <div style={{ fontSize: 13 }}>
              Create the first Seven-Generation Plan to begin thinking across time.
            </div>
          </div>
        ) : (
          plans.map(p => (
            <PlanCard
              key={p._id}
              plan={p}
              isSelected={selectedId === p._id}
              onClick={() => setSelectedId(p._id === selectedId ? null : p._id)}
              onDelete={handleDelete}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>

      {/* ── Right pane: plan detail ──────────────────────────────────────── */}
      {selectedId && (
        <div style={{ flex: 1, padding: '24px 28px', overflowY: 'auto', minWidth: 0 }}>
          <PlanDetail
            key={selectedId}
            planId={selectedId}
            tenants={tenants}
            isAdmin={isAdmin}
            onUpdated={loadPlans}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}

      {/* ── Create modal ─────────────────────────────────────────────────── */}
      {showCreator && (
        <PlanEditor
          tenants={tenants}
          onSubmit={handleCreate}
          onClose={() => setShowCreator(false)}
        />
      )}
    </div>
  );
}
